// Shared modal/dialog primitive. Backdrop + escape + click-outside-to-close.

import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { shouldClose } from './dialogDismiss';

export function Dialog({ open, onClose, title, children, footer, size = 'md', dismissable = true }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  // When false, the backdrop click and Escape no longer dismiss the dialog — only
  // explicit exits (header X, Cancel, a footer action). Use for destructive confirms
  // so a stray click can't close the modal and wipe in-progress input.
  dismissable?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && shouldClose('escape', dismissable)) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, dismissable]);

  if (!open) return null;
  const w = size === 'sm' ? 'max-w-md' : size === 'md' ? 'max-w-2xl' : size === 'lg' ? 'max-w-4xl' : 'max-w-6xl';
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { if (shouldClose('backdrop', dismissable)) onClose(); }}>
      <div className={`bg-surface border border-border rounded-lg shadow-xl w-full ${w} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </header>
        <div className="overflow-auto p-4 flex-1">{children}</div>
        {footer && <footer className="p-4 border-t border-border flex gap-2 justify-end">{footer}</footer>}
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false, dismissable = true }: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  // Destructive confirms pass dismissable={false} (#888): backdrop click and
  // Escape no longer close the dialog — only Cancel, the header X, or Confirm.
  dismissable?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      dismissable={dismissable}
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={() => { onConfirm(); onClose(); }} className={`btn ${danger ? 'bg-danger text-white hover:bg-danger/80' : 'btn-primary'}`}>{confirmLabel}</button>
        </>
      }>
      <p className="text-sm">{message}</p>
    </Dialog>
  );
}
